import { Dialog } from "../../ui/layers";
import { useManagedSigningExperienceCopy } from "./managedSigningExperienceCatalog";
import { matchesDirectorySearch } from "../../ui/directorySearch";
import { formatActiveNumber } from "../../i18n/formatting";
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  Eye,
  Fingerprint,
  LockKeyhole,
  RefreshCcw,
  Search,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";

import { useKeysTrustManagedCopy, type KeysTrustManagedMessageKey } from "./keysTrustManagedCatalog";
import { readTufHealth, type TufHealth } from "./tufHealthApi";
import "../../styles/keys-trust.css";

export type TrustHealth =
  | "healthy"
  | "missing"
  | "unavailable"
  | "insecure_permissions"
  | "invalid_public_key"
  | "optional_unconfigured"
  | "external_unreconciled";

type TrustAuthority = {
  authority_id: string;
  label: string;
  family: string;
  material_class: string;
  provider_kind: string;
  provider_reference: string;
  configured: boolean;
  required: boolean;
  release_required: boolean;
  lifecycle_state: string;
  health: TrustHealth;
  fingerprints_sha256: string[];
  consumers: string[];
  remediation: string | null;
  private_material: boolean;
  private_material_exposed: boolean;
  ui_mutation_supported: boolean;
};

export type TrustOverview = {
  contract_version: "kavoosh-keys-trust-overview.v1";
  environment: string;
  status: "healthy" | "attention" | "blocked";
  release_readiness: "ready" | "blocked" | "unknown";
  total_authorities: number;
  healthy_authorities: number;
  attention_authorities: number;
  external_unreconciled: number;
  private_material_exposed: boolean;
  authorities: TrustAuthority[];
};

type Family = "all" | "collector_release" | "collector_device_trust" | "evidence_custody" | "analysis_trust" | "platform_security" | "integrations";
type HealthFilter = "all" | "attention" | TrustHealth;
type KeysTrustView = "overview" | "inventory";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";
type KeysTrustTranslate = ReturnType<typeof useKeysTrustManagedCopy>["t"];

const FAMILY_KEYS: Record<Exclude<Family, "all">, KeysTrustManagedMessageKey> = {
  collector_release: "familyCollectorRelease",
  collector_device_trust: "familyCollectorDevice",
  evidence_custody: "familyEvidenceCustody",
  analysis_trust: "familyAnalysisTrust",
  platform_security: "familyPlatformSecurity",
  integrations: "familyIntegrations",
};

const HEALTH_KEYS: Record<TrustHealth, KeysTrustManagedMessageKey> = {
  healthy: "healthHealthy",
  missing: "healthMissing",
  unavailable: "healthUnavailable",
  insecure_permissions: "healthUnsafePermissions",
  invalid_public_key: "healthInvalidPublicKey",
  optional_unconfigured: "healthNotConfigured",
  external_unreconciled: "healthExternalCustody",
};

function familyLabel(value: string, t: KeysTrustTranslate): string {
  const key = FAMILY_KEYS[value as Exclude<Family, "all">];
  return key ? t(key) : value.replaceAll("_", " ");
}

function healthLabel(health: TrustHealth, t: KeysTrustTranslate): string {
  return t(HEALTH_KEYS[health]);
}

function readinessCopy(
  value: TrustOverview["release_readiness"],
  t: KeysTrustTranslate,
): { title: string; detail: string } {
  if (value === "ready") return {
    title: t("readinessReadyTitle"),
    detail: t("readinessReadyBody"),
  };
  if (value === "blocked") return {
    title: t("readinessBlockedTitle"),
    detail: t("readinessBlockedBody"),
  };
  return {
    title: t("readinessReconcileTitle"),
    detail: t("readinessReconcileBody"),
  };
}

function shortFingerprint(value: string): string {
  return `${value.slice(0, 12)}…${value.slice(-12)}`;
}

async function loadOverview(t: KeysTrustTranslate): Promise<TrustOverview> {
  const response = await fetch(`${API_BASE}/admin/keys-trust`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    if (response.status === 401) throw new Error("SESSION_UNAVAILABLE");
    if (response.status === 403) throw new Error(t("errorAdminRequired"));
    const body = await response.json().catch(() => ({})) as { detail?: unknown };
    throw new Error(typeof body.detail === "string" ? body.detail : t("errorLoad"));
  }
  const value = await response.json() as TrustOverview;
  if (value.contract_version !== "kavoosh-keys-trust-overview.v1" || value.private_material_exposed !== false || !Array.isArray(value.authorities)) {
    throw new Error(t("errorContract"));
  }
  return value;
}

export function KeysTrustPage({
  view = "overview",
  onOpenInventory,
  onOpenTuf,
  onSessionUnavailable,
}: {
  view?: KeysTrustView;
  onOpenInventory?: () => void;
  onOpenTuf?: () => void;
  onSessionUnavailable?: () => void;
}) {
  const { t } = useKeysTrustManagedCopy();
  const c=useManagedSigningExperienceCopy();
  const [selected,setSelected]=useState<TrustAuthority|null>(null);
  const [data, setData] = useState<TrustOverview | null>(null);
  const [tufHealth, setTufHealth] = useState<TufHealth | null>(null);
  const [tufHealthError, setTufHealthError] = useState<string | null>(null);
  const [family, setFamily] = useState<Family>("all");
  const [health, setHealth] = useState<HealthFilter>("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    setTufHealthError(null);
    try {
      setData(await loadOverview(t));
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : t("errorLoad");
      if (message === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(message);
      setLoading(false);
      return;
    }

    try {
      setTufHealth(await readTufHealth());
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : t("tufHealthLoadError");
      setTufHealth(null);
      if (message === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setTufHealthError(t("tufHealthLoadError"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, [t]);

  const visible = useMemo(() => (data?.authorities ?? []).filter((item) => {
    if (family !== "all" && item.family !== family) return false;
    if (health === "attention" && ["healthy", "optional_unconfigured"].includes(item.health)) return false;
    if (health !== "all" && health !== "attention" && item.health !== health) return false;
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return true;
    const searchable = [
      item.label,
      item.family,
      familyLabel(item.family, t),
      item.material_class,
      item.provider_kind,
      item.provider_reference,
      item.lifecycle_state,
      healthLabel(item.health, t),
      ...item.consumers,
      ...item.fingerprints_sha256,
    ].join(" ").toLowerCase();
    return matchesDirectorySearch(query,searchable);
  }), [data, family, health, query, t]);

  const releaseAuthorities = useMemo(
    () => (data?.authorities ?? []).filter((item) => item.release_required),
    [data],
  );
  const managedReleaseAttention = useMemo(
    () => releaseAuthorities.filter((item) => !["healthy", "external_unreconciled"].includes(item.health)),
    [releaseAuthorities],
  );
  const externalReleaseAttention = useMemo(
    () => releaseAuthorities.filter((item) => item.health === "external_unreconciled"),
    [releaseAuthorities],
  );
  const tufReleaseBlocking = Boolean(tufHealth?.release_blocking || tufHealthError);
  const managedReleaseBlockerCount = managedReleaseAttention.length + (tufReleaseBlocking ? 1 : 0);
  const effectiveReleaseReadiness: TrustOverview["release_readiness"] = tufReleaseBlocking
    ? "blocked"
    : data?.release_readiness ?? "unknown";
  const release = data ? readinessCopy(effectiveReleaseReadiness, t) : null;

  if (error) return <p className="admin-v12-form-error" role="alert">{error}</p>;
  if (loading && !data) return <section className="keys-trust-loading" aria-live="polite">{t("checkingProviders")}</section>;
  if (!data) return null;

  if (view === "inventory") {
    return <div className="keys-trust-page keys-trust-inventory-view">
      <section className="admin-v12-card keys-trust-inventory-card">
        <div className="admin-v12-card-heading keys-trust-inventory-heading">
          <div>
            <p className="admin-v12-eyebrow">{t("inventoryEyebrow")}</p>
            <h2>{t("inventoryTitle")}</h2>
            <p>{t("inventoryBody")}</p>
          </div>
          <button type="button" className="admin-v12-secondary" onClick={() => void refresh()} disabled={loading}>
            <RefreshCcw size={15} />{loading ? t("checking") : t("refresh")}
          </button>
        </div>

        <div className="keys-trust-controls" aria-label={t("filterAria")}>
          <label className="keys-trust-search">{t("search")}
            <span><Search size={15} /><input value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder={t("searchPlaceholder")} /></span>
          </label>
          <label>{t("family")}
            <select value={family} onChange={(event) => setFamily(event.currentTarget.value as Family)}>
              <option value="all">{t("allFamilies")}</option>
              {Object.entries(FAMILY_KEYS).map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}
            </select>
          </label>
          <label>{t("health")}
            <select value={health} onChange={(event) => setHealth(event.currentTarget.value as HealthFilter)}>
              <option value="all">{t("allStates")}</option>
              <option value="attention">{t("needsAttention")}</option>
              <option value="healthy">{t("healthHealthy")}</option>
              <option value="missing">{t("healthMissing")}</option>
              <option value="unavailable">{t("healthUnavailable")}</option>
              <option value="insecure_permissions">{t("healthUnsafePermissions")}</option>
              <option value="invalid_public_key">{t("healthInvalidPublicKey")}</option>
              <option value="external_unreconciled">{t("healthExternalCustody")}</option>
              <option value="optional_unconfigured">{t("healthNotConfigured")}</option>
            </select>
          </label>
          <div className="keys-trust-filter-result">
            <strong>{formatActiveNumber(visible.length)}</strong><span>{t("resultOf", { count: data.total_authorities })}</span>
            {(query || family !== "all" || health !== "all") && <button type="button" onClick={() => { setQuery(""); setFamily("all"); setHealth("all"); }}>{t("clearFilters")}</button>}
          </div>
        </div>

        <div className="admin-v12-table-wrap keys-trust-table-wrap">
          <table>
            <thead><tr><th>{t("columnAuthority")}</th><th>{t("columnState")}</th><th>{t("columnFamily")}</th><th>{t("columnCustody")}</th><th>{t("columnRelease")}</th><th>{t("columnUsedBy")}</th><th><span className="sr-only">{t("inventoryTitle")}</span></th></tr></thead>
            <tbody>
              {visible.map((item) => <tr key={item.authority_id}>
                <td>
                  <div className="keys-trust-authority-name">
                    <span className="keys-trust-icon">{item.private_material ? <LockKeyhole size={15} /> : <Fingerprint size={15} />}</span>
                    <span><strong>{item.label}</strong></span>
                  </div>
                </td>
                <td><span className={`keys-trust-badge keys-trust-badge-${item.health}`}>{healthLabel(item.health, t)}</span><small className="keys-trust-lifecycle">{item.lifecycle_state.replaceAll("_", " ")}</small></td>
                <td>{familyLabel(item.family, t)}</td>
                <td>{item.provider_kind.replaceAll("_", " ")}</td>
                <td>{item.release_required ? t("required") : t("no")}</td>
                <td>{item.consumers.join(", ")}</td><td><button type="button" className="admin-v12-icon-button" aria-label={`${t("inventoryTitle")}: ${item.label}`} title={item.label} onClick={()=>setSelected(item)}><Eye size={17}/></button></td>
              </tr>)}
            </tbody>
          </table>
          {visible.length === 0 && <div className="admin-v12-empty">{t("noMatches")}</div>}
        </div>
      </section>
      <Dialog open={selected!==null} onDismiss={()=>setSelected(null)} title={selected?.label??t("inventoryTitle")} footer={<button type="button" className="admin-v12-secondary" onClick={()=>setSelected(null)}>{c("cancel")}</button>}>
        {selected && <><p><span className={`keys-trust-badge keys-trust-badge-${selected.health}`}>{healthLabel(selected.health,t)}</span></p><dl className="keys-authority-details"><dt>{t("columnFamily")}</dt><dd>{familyLabel(selected.family,t)}</dd><dt>{t("columnCustody")}</dt><dd><code>{selected.provider_kind}</code></dd><dt>{t("provider")}</dt><dd><code>{selected.provider_reference}</code></dd><dt>{t("fingerprintLabel")}</dt><dd>{selected.fingerprints_sha256.length ? selected.fingerprints_sha256.map(f=><code key={f}>{f}</code>):t("pending")}</dd><dt>{t("columnUsedBy")}</dt><dd>{selected.consumers.join(" · ")}</dd></dl>{selected.remediation && <aside className="keys-host-instructions"><AlertTriangle size={18}/><p>{selected.remediation}</p></aside>}<p>{t("boundaryBody")}</p></>}
      </Dialog>
    </div>;
  }

  return <div className="keys-trust-page keys-trust-overview-view">
    <section className={`keys-trust-release keys-trust-release-${effectiveReleaseReadiness}`} aria-label={t("readinessAria")}>
      <span>{effectiveReleaseReadiness === "ready" ? <ShieldCheck size={22} /> : <AlertTriangle size={22} />}</span>
      <div><p className="admin-v12-eyebrow">{t("readinessEyebrow")}</p><h2>{release?.title}</h2><p>{release?.detail}</p></div>
      <strong>{effectiveReleaseReadiness === "ready" ? t("stateReady") : effectiveReleaseReadiness === "blocked" ? t("stateBlocked") : t("stateUnknown")}</strong>
    </section>

    <div className="keys-trust-overview-grid">
      <section className="admin-v12-card keys-trust-attention-card">
        <div className="admin-v12-card-heading">
          <div><p className="admin-v12-eyebrow">{t("priorityEyebrow")}</p><h2>{t("priorityTitle")}</h2></div>
          <span>{formatActiveNumber(managedReleaseBlockerCount)}</span>
        </div>
        {managedReleaseBlockerCount ? <div className="keys-trust-action-list">
          {tufReleaseBlocking && <article key="tuf-public-update-trust">
            <span className="keys-trust-action-icon"><AlertTriangle size={15} /></span>
            <div>
              <strong>{t("tufHealth")}</strong>
              <small>{tufHealthError ? t("tufStatusUnavailable") : tufHealth?.status === "expired" ? t("tufStatusExpired") : t("stateBlocked")}</small>
              <p>{tufHealth?.detail ?? tufHealthError ?? t("readinessBlockedBody")}</p>
              {onOpenTuf && <button
                type="button"
                className="keys-trust-card-link"
                onClick={onOpenTuf}
              >{t("tufMaintenanceOpen")} <ChevronRight size={14} /></button>}
            </div>
          </article>}
          {managedReleaseAttention.slice(0, Math.max(0, 5 - (tufReleaseBlocking ? 1 : 0))).map((item) => <article key={item.authority_id}>
            <span className="keys-trust-action-icon"><AlertTriangle size={15} /></span>
            <div><strong>{item.label}</strong><small>{familyLabel(item.family, t)} · {healthLabel(item.health, t)}</small>{item.remediation && <p>{item.remediation}</p>}</div>
          </article>)}
        </div> : <div className="keys-trust-clear-state"><CheckCircle2 size={20} /><strong>{t("noManagedRemediation")}</strong></div>}
        <p className="keys-trust-scope-note">{t("scopeNote")}</p>
        <button type="button" className="keys-trust-card-link" onClick={onOpenInventory}>{t("openInventory")} <ChevronRight size={14} /></button>
      </section>

    </div>

    {externalReleaseAttention.length > 0 && <section className="admin-v12-card keys-trust-external-summary">
      <div><CircleHelp size={18} /><span><strong>{t("externalTitle", { count: externalReleaseAttention.length })}</strong><small>{t("externalBody")}</small></span></div>
      <button type="button" className="admin-v12-secondary" onClick={onOpenInventory}>{t("reviewExternal")}</button>
    </section>}

    <details className="keys-statistics-disclosure"><summary>{t("statsAria")}</summary>    <section className="keys-trust-stats" aria-label={t("statsAria")}>
      <article><span><Fingerprint size={17} />{t("trustInventoryStat")}</span><strong>{formatActiveNumber(data.total_authorities)}</strong><small>{t("environment", { name: data.environment })}</small></article>
      <article><span><ShieldCheck size={17} />{t("releaseAuthorities")}</span><strong>{formatActiveNumber(releaseAuthorities.length)}</strong><small>{t("requiredByFlows")}</small></article>
      <article><span><ShieldAlert size={17} />{t("releaseBlockers")}</span><strong>{formatActiveNumber(managedReleaseBlockerCount)}</strong><small>{t("managedRemediation")}</small></article>
      <article><span><CircleHelp size={17} />{t("externalReleaseCustody")}</span><strong>{formatActiveNumber(externalReleaseAttention.length)}</strong><small>{t("visibleNotManaged")}</small></article>
    </section>

</details>
    <aside className="keys-trust-boundary">
      <LockKeyhole size={18} />
      <div><strong>{t("boundaryTitle")}</strong><p>{t("boundaryBody")}</p></div>
    </aside>
  </div>;
}
